import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Grid,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Add,
  Email,
  LocationOn,
  CheckCircle,
  Delete as DeleteIcon,
  VerifiedUser,
  Logout
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { VendorLogo } from '../components/VendorLogo';
import { RegionSelect, StatusChip } from '../components/ui';
import { ValidatedTextField } from '../components/ui/ValidatedTextField';
import { validateEmail, validatePhone, normalizePhone, validateUrl, validatePostalCode, normalizePostalCode, validateTaxId, normalizeTaxId } from '../utils/inputValidators';
import { useAuth } from '../contexts/AuthContext';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { businessTypeLabel, businessTypeOptions } from '../utils/businessType';

interface StoreFormData {
  organizationName: string;
  businessType: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    /**
     * The real country name when `country` is the `'OTHER'` placeholder —
     * `COUNTRY_OPTIONS` only lists US/CA by code, everything else picks
     * "Other" and types the name here. Submitting `'OTHER'` itself as the
     * country loses the store's actual business identity (used for
     * approval review) and would seed locations with an invalid country.
     */
    otherCountryName: string;
  };
  /**
   * Business tax ID — a US EIN, a Canadian Business Number (BN), or a
   * free-form ID for anywhere else. Shape depends on `address.country`;
   * validated server-side by `storeOnboardingSchema`
   * (`src/validators/storeOnboarding.ts`).
   */
  taxId: string;
  contact: {
    email: string;
    phone: string;
    website?: string;
  };
  brandsSold: string[];
  authorizedVendors: string[];
  userInvites: Array<{
    email: string;
    role: 'admin' | 'employee';
  }>;
}

/** Countries offered in the address form. Tax-ID validation only special-cases US and CA. */
const COUNTRY_OPTIONS: Array<{ code: string; label: string }> = [
  { code: 'US', label: 'United States' },
  { code: 'CA', label: 'Canada' },
  { code: 'OTHER', label: 'Other' },
];

/** Label the tax ID field for the selected country instead of always saying "EIN". */
function taxIdLabel(country: string): string {
  if (country === 'US') return 'EIN (Employer Identification Number)';
  if (country === 'CA') return 'Business Number (BN)';
  return 'Tax ID';
}

function taxIdHelperText(country: string): string {
  if (country === 'US') return 'Format: NN-NNNNNNN';
  if (country === 'CA') return '9 digits, e.g. 123456789 (no RT0001 program id)';
  return 'Your business or government-issued tax ID';
}

/** The country to submit/display: the typed name when "Other" was picked, the code otherwise. */
function resolveCountry(address: StoreFormData['address']): string {
  return address.country === 'OTHER' ? address.otherCountryName.trim() : address.country;
}

interface Brand {
  id: string;
  name: string;
  type?: string;
  category?: string;
  logoUrl?: string;
  /** False when the catalog has no products for this brand. Missing means unknown. */
  hasProducts?: boolean;
}

const steps = ['Store Information', 'Brand Selection', 'Team Setup', 'Review & Create'];

export const StoreOrganizationSetupPage: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<StoreFormData>({
    organizationName: '',
    businessType: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'US',
      otherCountryName: ''
    },
    taxId: '',
    contact: {
      email: '',
      phone: '',
      website: ''
    },
    brandsSold: [],
    authorizedVendors: [],
    userInvites: []
  });
  const [newInviteEmail, setNewInviteEmail] = useState('');
  const [newInviteRole, setNewInviteRole] = useState<'admin' | 'employee'>('employee');
  const [brandViewMode, setBrandViewMode] = useState<'grid' | 'list'>('grid');
  const [loggingOut, setLoggingOut] = useState(false);

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { t } = useAppTranslation();
  const comingSoonLabel = t('onboarding.brandComingSoon');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Auto-save form data to localStorage
  useEffect(() => {
    const savedData = localStorage.getItem('storeSetupForm');
    if (savedData) {
      try {
        setFormData(JSON.parse(savedData));
      } catch (error) {
        console.error('Failed to parse saved form data:', error);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('storeSetupForm', JSON.stringify(formData));
  }, [formData]);

  // Fetch available brands
  const { data: brandsData, isLoading: brandsLoading } = useQuery({
    queryKey: ['store', 'brands'],
    queryFn: async () => {
      // Use makeApiCall for proper URL handling
      const { makeApiCall, getAuthHeaders } = await import('../utils/apiConfig');
      const headers = await getAuthHeaders();
      
      const response = await makeApiCall('/store/brands', { headers });

      if (!response.ok) {
        throw new Error('Failed to fetch brands');
      }

      return response.json();
    }
  });

  // Get current user's organization
  const { data: currentOrgData } = useQuery({
    queryKey: ['organizations', 'me'],
    queryFn: async () => {
      // Use makeApiCall for proper URL handling
      const { makeApiCall, getAuthHeaders } = await import('../utils/apiConfig');
      const headers = await getAuthHeaders();
      
      const response = await makeApiCall('/organizations/me', {
        headers
      });

      if (!response.ok) {
        throw new Error('Failed to fetch organization');
      }

      return response.json();
    }
  });
  void currentOrgData; // suppress unused warning (future steps may use it)

  // Create organization mutation
  const createOrganizationMutation = useMutation({
    mutationFn: async (data: StoreFormData) => {
      // Use makeApiCall for proper URL handling
      const { makeApiCall, getAuthHeaders } = await import('../utils/apiConfig');
      const headers = await getAuthHeaders();
      
      const response = await makeApiCall('/onboarding/complete-store', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          organizationName: data.organizationName,
          businessInfo: {
            businessName: data.organizationName,
            businessType: data.businessType
          },
          contactInfo: {
            primaryContactName: data.contact.email.split('@')[0],
            primaryContactEmail: data.contact.email,
            primaryContactPhone: data.contact.phone,
            title: 'Owner',
            website: data.contact.website
          },
          address: {
            streetAddress: data.address.street,
            city: data.address.city,
            state: data.address.state,
            zip: data.address.zipCode,
            country: resolveCountry(data.address)
          },
          taxId: normalizeTaxId(data.taxId, data.address.country),
          brandsSold: data.brandsSold,
          authorizedVendors: data.authorizedVendors
        })
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(
          errorBody?.error?.message ||
            errorBody?.message ||
            'Failed to create organization'
        );
      }

      return response.json();
    },
    onSuccess: (result) => {
      localStorage.removeItem('storeSetupForm');
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      // A new store still needs to add locations or defer that; the server
      // says where to go next (either /onboarding/store-locations or
      // straight to the dashboard).
      navigate(result?.data?.nextStep || '/app/dashboard');
    }
  });

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleInputChange = (field: string, value: any) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => {
        if (parent === 'address') {
          return {
            ...prev,
            address: {
              ...prev.address,
              [child]: value,
            },
          };
        }
        if (parent === 'contact') {
          return {
            ...prev,
            contact: {
              ...prev.contact,
              [child]: value,
            },
          };
        }
        // Fallback for unexpected nested field
        return {
          ...prev,
          [parent]: {
            ...(prev as any)[parent],
            [child]: value,
          },
        } as StoreFormData;
      });
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleBrandToggle = (brandId: string) => {
    setFormData(prev => {
      const isSelected = prev.brandsSold.includes(brandId);
      
      if (isSelected) {
        // Remove from both arrays
        return {
          ...prev,
          brandsSold: prev.brandsSold.filter(id => id !== brandId),
          authorizedVendors: prev.authorizedVendors.filter(id => id !== brandId)
        };
      } else {
        // Add to brandsSold (defaults to unauthorized)
        return {
          ...prev,
          brandsSold: [...prev.brandsSold, brandId]
        };
      }
    });
  };

  const handleAuthorizedToggle = (brandId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    setFormData(prev => {
      const isAuthorized = prev.authorizedVendors.includes(brandId);
      
      if (isAuthorized) {
        // Remove from authorized (but keep in brandsSold)
        return {
          ...prev,
          authorizedVendors: prev.authorizedVendors.filter(id => id !== brandId)
        };
      } else {
        // Add to authorized
        return {
          ...prev,
          authorizedVendors: [...prev.authorizedVendors, brandId]
        };
      }
    });
  };

  const handleAddInvite = () => {
    if (newInviteEmail && !formData.userInvites.some(invite => invite.email === newInviteEmail)) {
      setFormData(prev => ({
        ...prev,
        userInvites: [...prev.userInvites, {
          email: newInviteEmail,
          role: newInviteRole
        }]
      }));
      setNewInviteEmail('');
    }
  };

  const handleRemoveInvite = (index: number) => {
    setFormData(prev => ({
      ...prev,
      userInvites: prev.userInvites.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = () => {
    createOrganizationMutation.mutate(formData);
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
      setLoggingOut(false);
    }
  };

  const brands: Brand[] = brandsData?.data || [];

  // Prefer list mode automatically when the number of brands is large
  useEffect(() => {
    if (Array.isArray(brands) && brands.length > 24) {
      setBrandViewMode('list');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brands.length]);

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                Store Information
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Provide basic information about your camera store.
              </Typography>
            </Box>
            
            <Stack spacing={3}>
              <TextField
                fullWidth
                label="Store Name"
                value={formData.organizationName}
                onChange={(e) => handleInputChange('organizationName', e.target.value)}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }}
              />
              
              <FormControl fullWidth>
                <InputLabel>Business Type</InputLabel>
                <Select
                  value={formData.businessType}
                  label="Business Type"
                  onChange={(e) => handleInputChange('businessType', e.target.value)}
                  sx={{
                    borderRadius: 2
                  }}
                >
                  {businessTypeOptions(formData.businessType).map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                  <LocationOn sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Address
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Street Address"
                      value={formData.address.street}
                      onChange={(e) => handleInputChange('address.street', e.target.value)}
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="City"
                      value={formData.address.city}
                      onChange={(e) => handleInputChange('address.city', e.target.value)}
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <RegionSelect
                      fullWidth
                      size="medium"
                      country={formData.address.country}
                      label="State / Province"
                      value={formData.address.state}
                      onChange={(v) => handleInputChange('address.state', v)}
                      required={formData.address.country === 'US' || formData.address.country === 'CA'}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <ValidatedTextField
                      fullWidth
                      label="ZIP / Postal Code"
                      value={formData.address.zipCode}
                      onChange={(e) => handleInputChange('address.zipCode', e.target.value)}
                      validate={(v) => validatePostalCode(v, formData.address.country)}
                      normalize={(v) => normalizePostalCode(v, formData.address.country)}
                      onNormalized={(v) => handleInputChange('address.zipCode', v)}
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth required>
                      <InputLabel>Country</InputLabel>
                      <Select
                        value={formData.address.country}
                        label="Country"
                        onChange={(e) => {
                          const next = e.target.value as string;
                          // A state code from one country's list means nothing in another's.
                          if (next !== formData.address.country && (next === 'US' || next === 'CA' || formData.address.country === 'US' || formData.address.country === 'CA')) {
                            handleInputChange('address.state', '');
                          }
                          handleInputChange('address.country', next);
                        }}
                        sx={{ borderRadius: 2 }}
                      >
                        {COUNTRY_OPTIONS.map((option) => (
                          <MenuItem key={option.code} value={option.code}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  {formData.address.country === 'OTHER' && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Country name"
                        value={formData.address.otherCountryName}
                        onChange={(e) => handleInputChange('address.otherCountryName', e.target.value)}
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2
                          }
                        }}
                      />
                    </Grid>
                  )}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <ValidatedTextField
                      fullWidth
                      label={taxIdLabel(formData.address.country)}
                      helperText={taxIdHelperText(formData.address.country)}
                      value={formData.taxId}
                      onChange={(e) => handleInputChange('taxId', e.target.value)}
                      validate={(v) => validateTaxId(v, formData.address.country)}
                      normalize={(v) => normalizeTaxId(v, formData.address.country)}
                      onNormalized={(v) => handleInputChange('taxId', v)}
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                  <Email sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Contact Information
                </Typography>
                <ValidatedTextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.contact.email}
                  onChange={(e) => handleInputChange('contact.email', e.target.value)}
                  validate={validateEmail}
                  required
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
                <ValidatedTextField
                  fullWidth
                  label="Phone"
                  value={formData.contact.phone}
                  onChange={(e) => handleInputChange('contact.phone', e.target.value)}
                  validate={validatePhone}
                  normalize={normalizePhone}
                  onNormalized={(v) => handleInputChange('contact.phone', v)}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
                <ValidatedTextField
                  fullWidth
                  label="Website"
                  value={formData.contact.website || ''}
                  onChange={(e) => handleInputChange('contact.website', e.target.value)}
                  validate={(v) => validateUrl(v, { allowSchemeless: true })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Box>
            </Stack>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Brand Selection
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Select the brands you sell. Use search for faster selection. Mark authorized vendors.
                </Typography>
              </Box>
              <FormControl size="small">
                <InputLabel>View</InputLabel>
                <Select
                  label="View"
                  value={brandViewMode}
                  onChange={(e) => setBrandViewMode(e.target.value as 'grid' | 'list')}
                >
                  <MenuItem value="grid">Grid</MenuItem>
                  <MenuItem value="list">Searchable list</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {brandsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={48} />
              </Box>
            ) : brands.length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                No brands available at this time. Please contact support.
              </Alert>
            ) : brandViewMode === 'list' ? (
              <Box>
                {/* Searchable multi-select list */}
                <Box sx={{ mb: 2 }}>
                  {/* Using MUI Autocomplete with checkboxes */}
                  {/* We avoid importing directly at top to keep bundle size minimal in other routes */}
                </Box>
                {/* Inline implementation to avoid new components: */}
                {/* @ts-ignore - dynamic import style Autocomplete usage below */}
                <Box>
                  {/* We keep the code simple: build a custom list with filter */}
                  <TextField
                    fullWidth
                    placeholder="Search brands (type to filter)"
                    onChange={(e) => {
                      // Simple filter by hiding non-matching rows
                      const q = e.target.value.toLowerCase();
                      const container = document.getElementById('brand-list-container');
                      if (!container) return;
                      const children = Array.from(container.children) as HTMLElement[];
                      children.forEach(child => {
                        const nameEl = child.querySelector('[data-brand-name]') as HTMLElement | null;
                        const name = nameEl?.innerText?.toLowerCase() || '';
                        child.style.display = !q || name.includes(q) ? '' : 'none';
                      });
                    }}
                    sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <Box sx={{
                    maxHeight: 360,
                    overflow: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                  }}
                    id="brand-list-container"
                  >
                    {brands.map((brand) => {
                      const isSelected = formData.brandsSold.includes(brand.id);
                      const isAuthorized = formData.authorizedVendors.includes(brand.id);
                      return (
                        <Box
                          key={brand.id}
                          onClick={() => handleBrandToggle(brand.id)}
                          sx={{
                            display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1,
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                            borderBottom: '1px solid', borderColor: 'divider'
                          }}
                        >
                          <VendorLogo brand={brand.name} logoUrl={brand.logoUrl} size={28} showTooltip={false} />
                          <Typography sx={{ flex: 1 }} data-brand-name>
                            {brand.name}
                          </Typography>
                          {brand.hasProducts === false && (
                            <StatusChip label={comingSoonLabel} tone="neutral" />
                          )}
                          <Chip size="small" label={isSelected ? 'Selected' : 'Select'} color={isSelected ? 'primary' : 'default'} />
                          {isSelected && (
                            <Chip
                              size="small"
                              label={isAuthorized ? 'Authorized' : 'Mark authorized'}
                              color={isAuthorized ? 'success' : 'default'}
                              icon={isAuthorized ? <VerifiedUser fontSize="small" /> : undefined}
                              onClick={(e) => handleAuthorizedToggle(brand.id, e)}
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>

                {formData.brandsSold.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                      Selected ({formData.brandsSold.length})
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {formData.brandsSold.map((id) => {
                        const b = brands.find(x => x.id === id);
                        const isAuthorized = formData.authorizedVendors.includes(id);
                        return b ? (
                          <Chip
                            key={id}
                            label={b.name}
                            size="small"
                            icon={isAuthorized ? <VerifiedUser fontSize="small" /> : undefined}
                            onDelete={() => handleBrandToggle(id)}
                            deleteIcon={<DeleteIcon />}
                            color={isAuthorized ? 'success' : 'primary'}
                          />
                        ) : null;
                      })}
                    </Stack>
                  </Box>
                )}
              </Box>
            ) : (
              // Grid mode (original tiles), still useful for smaller lists
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                    lg: 'repeat(4, 1fr)'
                  },
                  gap: 2
                }}
              >
                {brands.map((brand) => {
                  const isSelected = formData.brandsSold.includes(brand.id);
                  const isAuthorized = formData.authorizedVendors.includes(brand.id);
                  
                  return (
                    <Card 
                      key={brand.id}
                      sx={{ 
                        cursor: 'pointer',
                        border: isSelected ? 2 : 1,
                        borderColor: isSelected 
                          ? isAuthorized ? 'success.main' : 'primary.main'
                          : 'divider',
                        borderRadius: 2,
                        transition: 'all 0.2s ease-in-out',
                        position: 'relative',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: isSelected 
                            ? isAuthorized 
                              ? '0 8px 16px rgba(76, 175, 80, 0.2)' 
                              : '0 8px 16px rgba(25, 118, 210, 0.2)'
                            : 4,
                        },
                        backgroundColor: isSelected 
                          ? isAuthorized 
                            ? 'rgba(76, 175, 80, 0.05)' 
                            : 'rgba(25, 118, 210, 0.05)'
                          : 'background.paper'
                      }}
                      onClick={() => handleBrandToggle(brand.id)}
                    >
                      <CardContent sx={{ 
                        textAlign: 'center',
                        pb: 1,
                        '&:last-child': { pb: 1 }
                      }}>
                        <Box
                          sx={{
                            width: 64,
                            height: 64,
                            mx: 'auto',
                            mb: 1.5,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: isSelected ? 2 : '1px solid',
                            borderColor: isSelected 
                              ? (isAuthorized ? 'success.main' : 'primary.main')
                              : 'divider',
                            bgcolor: 'white'
                          }}
                        >
                          <VendorLogo brand={brand.name} logoUrl={brand.logoUrl} size={64} showTooltip={false} />
                        </Box>
                        <Typography 
                          variant="subtitle1" 
                          sx={{ 
                            fontWeight: isSelected ? 600 : 500,
                            mb: 0.5
                          }}
                        >
                          {brand.name}
                        </Typography>
                        {brand.hasProducts === false && (
                          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
                            <StatusChip label={comingSoonLabel} tone="neutral" />
                          </Box>
                        )}
                        {brand.category && (
                          <Chip 
                            label={brand.category} 
                            size="small" 
                            sx={{ 
                              mt: 0.5,
                              fontSize: '0.7rem',
                              height: 20
                            }}
                          />
                        )}
                        {isSelected && (
                          <Chip
                            size="small"
                            label={isAuthorized ? 'Authorized' : 'Mark authorized'}
                            color={isAuthorized ? 'success' : 'default'}
                            icon={<VerifiedUser fontSize="small" />}
                            onClick={(e) => handleAuthorizedToggle(brand.id, e)}
                            sx={{ position: 'absolute', top: 8, right: 8 }}
                          />
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            )}

            {formData.brandsSold.length > 0 && (
              <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  Selected Brands ({formData.brandsSold.length})
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                  {formData.brandsSold.map(brandId => {
                    const brand = brands.find(b => b.id === brandId);
                    const isAuthorized = formData.authorizedVendors.includes(brandId);
                    return brand ? (
                      <Chip
                        key={brandId}
                        label={brand.name}
                        size="small"
                        icon={isAuthorized ? <VerifiedUser fontSize="small" /> : undefined}
                        color={isAuthorized ? 'success' : 'primary'}
                        sx={{ fontWeight: 500 }}
                      />
                    ) : null;
                  })}
                </Box>
              </Box>
            )}
          </Box>
        );

      case 2:
        return (
          <Box>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                Team Setup
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Invite team members to join your organization. You can skip this step and invite later.
              </Typography>
            </Box>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Add Team Member
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <ValidatedTextField
                  label="Email"
                  type="email"
                  value={newInviteEmail}
                  onChange={(e) => setNewInviteEmail(e.target.value)}
                  validate={validateEmail}
                  sx={{ flexGrow: 1 }}
                />
                <FormControl sx={{ minWidth: 120 }}>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={newInviteRole}
                    label="Role"
                    onChange={(e) => setNewInviteRole(e.target.value as 'admin' | 'employee')}
                  >
                    <MenuItem value="employee">Employee</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleAddInvite}
                  disabled={!newInviteEmail}
                >
                  Add
                </Button>
              </Stack>
            </Box>

            {formData.userInvites.length > 0 && (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Pending Invitations
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Email</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.userInvites.map((invite, index) => (
                        <TableRow key={index}>
                          <TableCell>{invite.email}</TableCell>
                          <TableCell>
                            <Chip 
                              label={invite.role} 
                              color={['admin', 'super_admin'].includes(invite.role as string) ? 'primary' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveInvite(index)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        );

      case 3:
        return (
          <Box>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                Review & Create
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Review your organization details before creating. You can go back to make changes.
              </Typography>
            </Box>

            {createOrganizationMutation.isError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {createOrganizationMutation.error instanceof Error
                  ? createOrganizationMutation.error.message
                  : 'Failed to create organization'}
              </Alert>
            )}
            
            <Stack spacing={3}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Store Information
                  </Typography>
                  <Typography variant="body2"><strong>Name:</strong> {formData.organizationName}</Typography>
                  <Typography variant="body2"><strong>Type:</strong> {businessTypeLabel(formData.businessType)}</Typography>
                  <Typography variant="body2"><strong>Email:</strong> {formData.contact.email}</Typography>
                  <Typography variant="body2"><strong>Phone:</strong> {formData.contact.phone}</Typography>
                  {formData.contact.website && (
                    <Typography variant="body2"><strong>Website:</strong> {formData.contact.website}</Typography>
                  )}
                  <Typography variant="body2">
                    <strong>Address:</strong> {formData.address.street}, {formData.address.city}, {formData.address.state} {formData.address.zipCode}, {formData.address.country === 'OTHER' ? formData.address.otherCountryName : (COUNTRY_OPTIONS.find(o => o.code === formData.address.country)?.label || formData.address.country)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{taxIdLabel(formData.address.country)}:</strong> {formData.taxId}
                  </Typography>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    Selected Brands ({formData.brandsSold.length})
                  </Typography>
                  {formData.authorizedVendors.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Authorized Vendors:
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                        {formData.authorizedVendors.map(brandId => {
                          const brand = brands.find(b => b.id === brandId);
                          return brand ? (
                            <Chip
                              key={brandId}
                              label={brand.name}
                              size="small"
                              icon={<VerifiedUser fontSize="small" />}
                              color="success"
                              sx={{ fontWeight: 500 }}
                            />
                          ) : null;
                        })}
                      </Stack>
                    </Box>
                  )}
                  {formData.brandsSold.filter(id => !formData.authorizedVendors.includes(id)).length > 0 && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Other Brands:
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                        {formData.brandsSold
                          .filter(id => !formData.authorizedVendors.includes(id))
                          .map(brandId => {
                            const brand = brands.find(b => b.id === brandId);
                            return brand ? (
                              <Chip key={brandId} label={brand.name} size="small" color="primary" />
                            ) : null;
                          })}
                      </Stack>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {formData.userInvites.length > 0 && (
                <Card>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      Team Invitations ({formData.userInvites.length})
                    </Typography>
                    {formData.userInvites.map((invite, index) => (
                      <Typography key={index} variant="body2">
                        {invite.email} - {invite.role}
                      </Typography>
                    ))}
                  </CardContent>
                </Card>
              )}

              <Alert severity="info">
                <Typography variant="body2">
                  Your organization will be created and team invitations will be sent. 
                  You can manage your organization settings after creation.
                </Typography>
              </Alert>
            </Stack>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        py: 4,
        px: { xs: 2, sm: 3 }
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="text"
            startIcon={<Logout />}
            onClick={handleLogout}
            disabled={loggingOut}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            {loggingOut ? 'Signing out...' : 'Sign out'}
          </Button>
        </Box>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              mb: 1,
            }}
          >
            Store Setup
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              maxWidth: 600,
              mx: 'auto'
            }}
          >
            Let's get your camera store set up in just a few steps
          </Typography>
        </Box>

        <Paper
          sx={{
            p: { xs: 3, sm: 4, md: 5 },
            borderRadius: 3,
          }}
        >
          <Stepper 
            activeStep={activeStep} 
            orientation={isMobile ? 'vertical' : 'horizontal'}
            sx={{ 
              mb: { xs: 4, sm: 5 },
              '& .MuiStepLabel-label': {
                fontWeight: 500
              },
              '& .MuiStepLabel-active .MuiStepLabel-label': {
                fontWeight: 600,
                color: 'primary.main'
              },
              '& .MuiStepIcon-root.Mui-completed': {
                color: 'success.main'
              }
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ minHeight: 400, mb: 4 }}>
            {renderStepContent(activeStep)}
          </Box>

          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              pt: 3,
              borderTop: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              size="large"
              sx={{ 
                minWidth: 120,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Back
            </Button>
            <Box>
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={createOrganizationMutation.isPending}
                  startIcon={<CheckCircle />}
                  size="large"
                  sx={{
                    minWidth: 200,
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  {createOrganizationMutation.isPending ? 'Creating...' : 'Create Organization'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={
                    (activeStep === 0 && (
                      !formData.organizationName ||
                      !formData.businessType ||
                      !formData.contact.email ||
                      !formData.address.street ||
                      !formData.address.city ||
                      // State/province is only required for US/CA — not every
                      // country divides addresses that way (mirrors the
                      // server's storeOnboardingSchema).
                      ((formData.address.country === 'US' || formData.address.country === 'CA') && !formData.address.state) ||
                      !formData.address.zipCode ||
                      !formData.address.country ||
                      (formData.address.country === 'OTHER' && !formData.address.otherCountryName.trim()) ||
                      !formData.taxId ||
                      validateTaxId(formData.taxId, formData.address.country) !== null
                    )) ||
                    (activeStep === 1 && formData.brandsSold.length === 0)
                  }
                  size="large"
                  sx={{
                    minWidth: 120,
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default StoreOrganizationSetupPage;